import { supabase } from '@/integrations/supabase/client';

export interface AccountMatch {
  id: string;
  account_number: string;
  similarity: number;
  customer_name?: string;
  service_type?: string;
}

export class AccountMatchingService {
  /**
   * Find exact match by account number
   */
  static async findAccount(accountNumber: string, workspaceId: string) {
    const { data, error } = await supabase
      .from('accounts')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('account_number', accountNumber)
      .maybeSingle();
    
    return { account: data, error };
  }
  
  /**
   * Find similar accounts using fuzzy matching (ILIKE for now, can be enhanced with pg_trgm)
   */
  static async findSimilarAccounts(accountNumber: string, workspaceId: string): Promise<AccountMatch[]> {
    // Simple fuzzy matching - can be enhanced with Levenshtein distance or pg_trgm
    const { data, error } = await supabase
      .from('accounts')
      .select('id, account_number, service_type')
      .eq('workspace_id', workspaceId)
      .ilike('account_number', `%${accountNumber}%`)
      .limit(5);
    
    if (error || !data) return [];
    
    // Calculate simple similarity score based on string overlap
    return data.map(account => ({
      ...account,
      similarity: this.calculateSimilarity(accountNumber, account.account_number)
    })).sort((a, b) => b.similarity - a.similarity);
  }
  
  /**
   * Create onboarding request when account not found
   */
  static async createOnboardingRequest(data: {
    workspaceId: string;
    billId?: string;
    queueItemId?: string;
    rawAccountNumber: string;
    rawCustomerName?: string;
    suggestedAccountId?: string;
    similarityScore?: number;
    createdBy: string;
  }) {
    const { data: request, error } = await supabase
      .from('onboarding_requests')
      .insert({
        workspace_id: data.workspaceId,
        bill_id: data.billId,
        queue_item_id: data.queueItemId,
        raw_account_number: data.rawAccountNumber,
        raw_customer_name: data.rawCustomerName,
        suggested_account_id: data.suggestedAccountId,
        similarity_score: data.similarityScore,
        created_by: data.createdBy,
        status: 'pending'
      })
      .select()
      .single();
    
    return { request, error };
  }
  
  /**
   * Simple string similarity calculation (can be replaced with Levenshtein)
   */
  private static calculateSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) return 1.0;
    
    const editDistance = this.editDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }
  
  /**
   * Calculate edit distance between two strings
   */
  private static editDistance(str1: string, str2: string): number {
    const costs: number[] = [];
    for (let i = 0; i <= str1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= str2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (str1.charAt(i - 1) !== str2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[str2.length] = lastValue;
    }
    return costs[str2.length];
  }
}
